import React, { useState, useMemo } from 'react';
import { Modal } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { FilterableSelect } from '@heartinternet/hi-ui';
import { Accordion } from '@heartinternet/hi-ui';
import type { AssignableDomain } from '../types';
import './AssignDomainModal.css';

interface AssignDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  isReassign: boolean;
  currentDomain?: string;
  assignableDomains: AssignableDomain[];
  showDnsUpdateFeature?: boolean;
  onConfirm: (
    domainName: string,
    updateDns: boolean
  ) => Promise<{ success: boolean; error?: string }>;
}

export const AssignDomainModal: React.FC<AssignDomainModalProps> = ({
  isOpen,
  onClose,
  isReassign,
  currentDomain,
  assignableDomains,
  showDnsUpdateFeature = false,
  onConfirm,
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [updateDns, setUpdateDns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usableOptions = useMemo(
    () =>
      assignableDomains
        .filter((d) => d.is_assignable)
        .map((d) => ({ value: d.domain_name, label: d.domain_name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [assignableDomains]
  );

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    const result = await onConfirm(selectedDomain!, updateDns);
    setLoading(false);
    if (!result.success) {
      setError(result.error ?? 'An unexpected error occurred.');
      return;
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedDomain(null);
    setUpdateDns(true);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleDomainChange = (value: string | null) => {
    setError(null);
    setSelectedDomain(value);
  };

  const helpItems = [
    {
      id: 'cant-see-domain',
      label: "I can't see my domain",
      content: (
        <div className="hi-ui-assign-domain-modal__help-content">
          <Text variant="body-small">
            This dropdown shows active and reference domains in your account. If you can&apos;t see
            the domain you&apos;re looking for check you own it. You may also need to transfer it in
            from another provider. If you require a solution and don&apos;t have a spare domain
            name, you can create a domain reference.
          </Text>
          <Text variant="body-small">
            You can <Link href="https://www.heartinternet.uk/domain-names">purchase a domain.</Link>
          </Text>
          <Text variant="body-small">
            <Link href="/manage/transfer.cgi">Transfer a domain</Link> from another provider.
          </Text>
          <Text variant="body-small" inline>
            Or, <Link href="/manage/soft_transfer.cgi">create a domain reference.</Link>
          </Text>
        </div>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isReassign ? 'Change package domain' : 'Assign a Domain'}
      size="medium"
    >
      <div className="hi-ui-assign-domain-modal">
        {isReassign ? (
          <div>
            <Text variant="body-small">Change the existing primary domain for this package.</Text>
            <div className="hi-ui-assign-domain-modal__current">
              <Text variant="body-small" inline>
                Currently:
              </Text>
              <Text variant="body-small" inline bold>
                {currentDomain}
              </Text>
            </div>
          </div>
        ) : (
          <div>
            <Text variant="body-small">
              Assigning a domain activates your package. Select your domain from the drop down
              below.
            </Text>
            <Text variant="body-small">
              You can change the assigned domain later. If your domain uses Heart Internet
              nameservers, we can automatically point your website&apos;s DNS records at this
              package. Your mail records (including MX) are left unchanged, so your email keeps
              working as it does now.
            </Text>
          </div>
        )}

        <FilterableSelect
          options={usableOptions}
          value={selectedDomain ?? undefined}
          onChange={handleDomainChange}
          placeholder="Choose a domain"
          disabled={usableOptions.length === 0}
          fullWidth
          aria-label="Select a domain"
        />

        {showDnsUpdateFeature && !isReassign && usableOptions.length > 0 && (
          <Checkbox
            label="Automatically update DNS"
            checked={updateDns}
            onChange={(e) => setUpdateDns(e.target.checked)}
          />
        )}

        {usableOptions.length === 0 ? (
          <div>
            <Text variant="body" className="hi-ui-assign-domain-modal__no-options">
              There are no domains found in your account. You will need to add a domain to continue.
            </Text>
            <div className="hi-ui-assign-domain-modal__help-content">
              <Text variant="body-small">
                You can{' '}
                <Link href="https://www.heartinternet.uk/domain-names">purchase a domain.</Link>
              </Text>
              <Text variant="body-small">
                <Link href="/manage/transfer.cgi">Transfer a domain</Link> from another provider.
              </Text>
              <Text variant="body-small" inline>
                Or, <Link href="/manage/soft_transfer.cgi">create a domain reference.</Link>
              </Text>
            </div>
          </div>
        ) : (
          <Accordion items={helpItems} iconStyle="plus-minus" defaultExpandedItems={[]} />
        )}

        {error && <Notice variant="error">{error}</Notice>}

        <div className="hi-ui-assign-domain-modal__actions">
          <Button
            variant="primary"
            size="large"
            onClick={handleConfirm}
            disabled={!selectedDomain}
            loading={loading}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
};
